import { formatNumber } from "sipli-fleet-core/utils/formatNumber";
import { App, Form } from "antd";
import type { TableProps } from "antd";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FiEdit2, FiPlusCircle, FiTrash2 } from "react-icons/fi";
import { CustomButton, CustomInput, CustomSelect, CustomTable } from "sipli-fleet-core/components";
import CustomSharedTitleSection from "sipli-fleet-core/components/CustomSharedTitleSection/CustomSharedTitleSection";
import {
  useMutationCreateTirePreset,
  useMutationUpdateTirePreset,
  useQueryGetTirePresetById,
} from "src/queries/administrator/tirePreset.queries";
import type {
  TirePresetConfiguration,
  TirePresetRecord,
} from "src/services/administrator/tirePresetService";
import { useQueryGetUnitManagementVehicleTypeLookup } from "src/queries/unitManagement.queries";
import type { UnitManagementLookupItem } from "src/services/unitManagementService";
import AddTireConfigModal from "./AddTireConfigModal";

export interface TirePresetConfigRow {
  id?: string | number;
  side: string;
  position: string;
  temperature: string;
  pressure: string;
  make: string;
  makeId?: string | number;
  model: string;
  modelId?: string | number;
  measure: string;
  measureId?: string | number;
  attached_unit: string;
  tireKilometers?: string | number;
  vehicleKilometersAtInstall?: string | number;
}

interface AxisConfig {
  id: string;
  distance?: string | number;
  rows: TirePresetConfigRow[];
}

interface TirePresetConfigProps {
  onCancel?: () => void;
}

interface TirePresetConfigFormValues {
  name: string;
  vehicleTypeId: string | number;
}

type SelectValue = string | number;

type SelectOption = {
  label: string;
  value: SelectValue;
};

const getLookupValue = (item: UnitManagementLookupItem): SelectValue | undefined => {
  const value = item.id ?? item.value;
  return typeof value === "string" || typeof value === "number" ? value : undefined;
};

const getLookupLabel = (item: UnitManagementLookupItem) => {
  const label = item.name ?? item.label ?? item.title ?? item.code ?? item.value ?? item.id;
  return String(label ?? "");
};

const pickLookupArray = (value: unknown): UnitManagementLookupItem[] => {
  if (!Array.isArray(value)) return [];

  return value.filter(
    (item): item is UnitManagementLookupItem => Boolean(item) && typeof item === "object"
  );
};

const extractLookupItems = (value: unknown): UnitManagementLookupItem[] => {
  if (Array.isArray(value)) return pickLookupArray(value);
  if (!value || typeof value !== "object") return [];

  const record = value as Record<string, unknown>;
  const responseItems = pickLookupArray(record.response);
  if (responseItems.length) return responseItems;

  if (record.response && typeof record.response === "object") {
    const nestedResponse = record.response as Record<string, unknown>;
    const nestedResources = pickLookupArray(nestedResponse.resources);
    if (nestedResources.length) return nestedResources;

    const nestedData = pickLookupArray(nestedResponse.data);
    if (nestedData.length) return nestedData;

    const nestedContent = pickLookupArray(nestedResponse.content);
    if (nestedContent.length) return nestedContent;
  }

  const dataItems = pickLookupArray(record.data);
  if (dataItems.length) return dataItems;

  return pickLookupArray(record.content);
};

const toOptions = (items: UnitManagementLookupItem[]): SelectOption[] =>
  items
    .map((item) => {
      const value = getLookupValue(item);
      const label = getLookupLabel(item);

      if (value === undefined || !label) return null;

      return { label, value };
    })
    .filter((item): item is SelectOption => Boolean(item));

const getDetailRecord = (payload?: unknown): Partial<TirePresetRecord> => {
  const response = payload as
    | TirePresetRecord
    | { tirePreset?: TirePresetRecord }
    | { data?: TirePresetRecord }
    | undefined;

  if (!response) return {};
  if ("tirePreset" in response && response.tirePreset) return response.tirePreset;
  if ("data" in response && response.data) return response.data;

  return response as TirePresetRecord;
};

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" ? (value as Record<string, unknown>) : {};

const getNestedText = (value: unknown, key: string): string => {
  const nestedValue = asRecord(value)[key];
  return nestedValue === undefined || nestedValue === null ? "" : String(nestedValue);
};

const getFirstVehicleId = (configuration: unknown): string | number | undefined => {
  const axes = asRecord(configuration);

  for (const axis of Object.values(axes)) {
    const details = asRecord(asRecord(axis).details);

    for (const detail of Object.values(details)) {
      const vehicleId = asRecord(detail).vehicleId;

      if (typeof vehicleId === "string" || typeof vehicleId === "number") return vehicleId;
    }
  }

  return undefined;
};

const configurationToAxes = (configuration: unknown): AxisConfig[] => {
  if (!configuration) return [];

  let configValue = configuration;

  if (typeof configValue === "string") {
    try {
      configValue = JSON.parse(configValue);
    } catch {
      return [];
    }
  }

  const entries = Array.isArray(configValue)
    ? configValue.flatMap((item) =>
        item && typeof item === "object" ? Object.entries(item as Record<string, unknown>) : []
      )
    : typeof configValue === "object"
      ? Object.entries(configValue as Record<string, unknown>)
      : [];

  return entries
    .map(([key, value]) => {
      if (Array.isArray(value)) {
        return {
          id: key,
          rows: value as TirePresetConfigRow[],
        };
      }

      const axisRecord = asRecord(value);
      const details = asRecord(axisRecord.details);
      const tireKeys = Array.isArray(axisRecord.tires)
        ? (axisRecord.tires as unknown[]).map(String)
        : Object.keys(details);
      const rows = tireKeys.map((tireKey) => {
        const detail = asRecord(details[tireKey]);

        return {
          id:
            typeof detail.id === "string" || typeof detail.id === "number" ? detail.id : undefined,
          side: tireKey.toUpperCase(),
          position: getNestedText(detail, "position"),
          temperature:
            getNestedText(detail, "temperature") || getNestedText(detail, "maxTemperature"),
          pressure: getNestedText(detail, "pressure") || getNestedText(detail, "maxPressure"),
          make: getNestedText(detail.tireMake, "name"),
          makeId: asRecord(detail.tireMake).id as string | number | undefined,
          model: getNestedText(detail.tireModel, "name"),
          modelId: asRecord(detail.tireModel).id as string | number | undefined,
          measure: getNestedText(detail.tireMeasure, "name"),
          measureId: asRecord(detail.tireMeasure).id as string | number | undefined,
          attached_unit: getNestedText(detail, "vehicleId"),
          tireKilometers: asRecord(detail).tireKilometers as string | number | undefined,
          vehicleKilometersAtInstall: asRecord(detail).vehicleKilometersAtInstall as
            | string
            | number
            | undefined,
        };
      });

      return {
        id: key,
        distance: axisRecord.distance as string | number | undefined,
        rows,
      };
    })
    .filter((axis) => axis.rows.length > 0);
};

const axesToConfiguration = (axes: AxisConfig[]): TirePresetConfiguration =>
  axes.reduce<TirePresetConfiguration>((configuration, axis, index) => {
    const axisKey = String(index + 1);
    const tires = axis.rows.map((row, rowIndex) =>
      (row.side || row.position || `tire-${rowIndex + 1}`).trim().toLowerCase()
    );

    configuration[axisKey] = {
      tires,
      details: axis.rows.reduce<TirePresetConfiguration[string]["details"]>(
        (details, row, rowIndex) => {
          const tireKey = tires[rowIndex] ?? `tire-${rowIndex + 1}`;
          const vehicleId = Number(row.attached_unit);

          details[tireKey] = {
            ...(row.id !== undefined ? { id: row.id } : {}),
            axle: index + 1,
            position: row.position,
            temperature: row.temperature,
            pressure: row.pressure,
            tireMake: {
              ...(row.makeId !== undefined ? { id: row.makeId } : {}),
              name: row.make,
            },
            tireModel: {
              ...(row.modelId !== undefined ? { id: row.modelId } : {}),
              name: row.model,
            },
            tireMeasure: {
              ...(row.measureId !== undefined ? { id: row.measureId } : {}),
              name: row.measure,
            },
            vehicleId: Number.isNaN(vehicleId) ? row.attached_unit : vehicleId,
            tireKilometers: row.tireKilometers ?? 0,
            vehicleKilometersAtInstall: row.vehicleKilometersAtInstall ?? 0,
          };

          return details;
        },
        {}
      ),
      distance: axis.distance ?? 0,
    };

    return configuration;
  }, {});

const TirePresetConfig = ({ onCancel }: TirePresetConfigProps) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { message } = App.useApp();
  const [form] = Form.useForm<TirePresetConfigFormValues>();
  const [axes, setAxes] = useState<AxisConfig[]>([]);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [targetAxisIndex, setTargetAxisIndex] = useState<number | null>(null);
  const [editingRow, setEditingRow] = useState<{
    axisIndex: number;
    rowIndex: number;
    values: TirePresetConfigRow;
  } | null>(null);

  const { data: vehicleTypeData, isLoading: isVehicleTypeLoading } =
    useQueryGetUnitManagementVehicleTypeLookup();
  const createMutation = useMutationCreateTirePreset();
  const updateMutation = useMutationUpdateTirePreset();
  const editId = searchParams.get("id") ?? undefined;
  const isEditing = Boolean(editId);
  const detailQuery = useQueryGetTirePresetById(editId, {
    enabled: isEditing,
  });
  const detailRecord = useMemo(
    () => getDetailRecord(detailQuery.data?.data?.response),
    [detailQuery.data?.data?.response]
  );
  const closeConfigScreen = () => {
    if (onCancel) {
      onCancel();
      return;
    }

    navigate("/administration/setups/tire-preset");
  };

  const vehicleTypeOptions = useMemo(() => {
    const payload = vehicleTypeData as { data?: unknown } | undefined;
    return toOptions(extractLookupItems(payload?.data));
  }, [vehicleTypeData]);

  useEffect(() => {
    if (!isEditing) return;
    if (!detailRecord.id) return;

    const vehicleType = asRecord(detailRecord.vehicleType);
    const vehicleTypeId = detailRecord.vehicleTypeId ?? vehicleType.id;

    form.setFieldsValue({
      name: detailRecord.name ?? "",
      vehicleTypeId:
        typeof vehicleTypeId === "string" || typeof vehicleTypeId === "number"
          ? vehicleTypeId
          : "",
    });
    setAxes(configurationToAxes(detailRecord.configuration));
  }, [detailRecord, form, isEditing]);

  const columns: TableProps<TirePresetConfigRow>["columns"] = useMemo(
    () => [
      { title: t("side"), dataIndex: "side", key: "side", sorter: true },
      { title: t("position"), dataIndex: "position", key: "position", sorter: true },
      {
        title: t("max_temperature"),
        dataIndex: "temperature",
        key: "temperature",
        render: (value) => formatNumber(value, i18n.language),
        sorter: true,
      },
      { title: t("max_pressure"), dataIndex: "pressure", key: "pressure", render: (value) => formatNumber(value, i18n.language), sorter: true },
      { title: t("make"), dataIndex: "make", key: "make", sorter: true },
      { title: t("model"), dataIndex: "model", key: "model", sorter: true },
      { title: t("measure"), dataIndex: "measure", key: "measure", sorter: true },
      {
        title: t("unit_attached"),
        dataIndex: "attached_unit",
        key: "attached_unit",
        sorter: true,
      },
      {
        title: t("actions"),
        key: "actions",
        width: 130,
        render: (_value, _record, rowIndex) => {
          const axisIndex = axes.findIndex((axis) => axis.rows[rowIndex] === _record);

          return (
            <div className="flex items-center gap-4">
              <CustomButton
                type="icon"
                buttonSize="small"
                icon={<FiEdit2 size={18} />}
                className="!text-primary !bg-transparent"
                aria-label={t("edit_tire_config")}
                onClick={() => {
                  if (axisIndex < 0) return;

                  setEditingRow({ axisIndex, rowIndex, values: _record });
                  setIsConfigModalOpen(true);
                }}
              />
              <CustomButton
                type="icon"
                buttonSize="small"
                icon={<FiTrash2 size={18} />}
                className="!text-red1 !bg-transparent"
                aria-label={t("delete")}
                onClick={() => {
                  if (axisIndex < 0) return;

                  setAxes((previous) =>
                    previous
                      .map((axis, index) =>
                        index === axisIndex
                          ? {
                              ...axis,
                              rows: axis.rows.filter((_row, index) => index !== rowIndex),
                            }
                          : axis
                      )
                      .filter((axis) => axis.rows.length > 0)
                  );
                }}
              />
            </div>
          );
        },
      },
    ],
    [axes, t, i18n.language]
  );

  const openNewAxisModal = () => {
    setEditingRow(null);
    setTargetAxisIndex(null);
    setIsConfigModalOpen(true);
  };

  const openAxisModal = (axisIndex: number) => {
    setEditingRow(null);
    setTargetAxisIndex(axisIndex);
    setIsConfigModalOpen(true);
  };

  const handleConfigSubmit = (values: TirePresetConfigRow) => {
    if (editingRow) {
      setAxes((previous) =>
        previous.map((axis, axisIndex) =>
          axisIndex === editingRow.axisIndex
            ? {
                ...axis,
                rows: axis.rows.map((row, rowIndex) =>
                  rowIndex === editingRow.rowIndex ? values : row
                ),
              }
            : axis
        )
      );
    } else if (targetAxisIndex !== null) {
      setAxes((previous) =>
        previous.map((axis, axisIndex) =>
          axisIndex === targetAxisIndex ? { ...axis, rows: [...axis.rows, values] } : axis
        )
      );
    } else {
      setAxes((previous) => [
        ...previous,
        {
          id: `axis-${Date.now()}`,
          rows: [values],
        },
      ]);
    }

    setEditingRow(null);
    setTargetAxisIndex(null);
    setIsConfigModalOpen(false);
  };

  const handleReset = () => {
    form.resetFields();
    setAxes([]);
    setEditingRow(null);
    setTargetAxisIndex(null);
    setIsConfigModalOpen(false);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();

    if (axes.length === 0) {
      message.error(t("tire_config_required"));
      return;
    }

    const vehicleId =
      detailRecord.vehicleId ??
      getFirstVehicleId(detailRecord.configuration) ??
      axes[0]?.rows[0]?.attached_unit ??
      0;

    const payload = {
      name: values.name.trim(),
      vehicleTypeId: values.vehicleTypeId,
      vehicleId,
      configuration: axesToConfiguration(axes),
      isBase: detailRecord.isBase ?? true,
      active: detailRecord.active ?? true,
      // vehicleTypeId: 0,
      // vehicleId: 0,
      // name: "string",
      // configuration: {
      //   additionalProp1: "string",
      //   additionalProp2: "string",
      //   additionalProp3: "string",
      // },
      // isBase: true,
      // active: true,
    };

    try {
      if (isEditing && editId) {
        await updateMutation.mutateAsync({
          id: editId,
          payload,
        });
        message.success(t("tire_preset_updated"));
      } else {
        await createMutation.mutateAsync(payload);
        message.success(t("tire_preset_created"));
      }

      handleReset();
      closeConfigScreen();
    } catch {
      message.error(t("setup_save_failed"));
    }
  };

  return (
    <section className="min-h-[calc(100vh-76px)] bg-gray1 pb-8 pl-2">
      <CustomSharedTitleSection
        titleKey="tire_preset_config"
        showBtn={false}
        titleClassName="!text-[25.23px] !font-[700]"
      />

      <Form form={form} layout="vertical" requiredMark={false}>
        <div className="grid w-full grid-cols-1 items-end gap-4 pr-4 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_160px]">
          <Form.Item
            name="name"
            label={t("tire_name")}
            rules={[{ required: true, whitespace: true, message: t("name_required") }]}
            className="mb-0! w-full"
          >
            <CustomInput placeholder={t("enter_name")} maxLength={64} />
          </Form.Item>

          <Form.Item
            name="vehicleTypeId"
            label={t("vehicle_type")}
            rules={[{ required: true, message: t("field_required") }]}
            className="mb-0! w-full"
          >
            <CustomSelect
              placeholder={t("select")}
              options={vehicleTypeOptions}
              loading={isVehicleTypeLoading}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>

          <CustomButton
            type="primary"
            label={t("add")}
            className="w-[120px]"
            onClick={openNewAxisModal}
          />
        </div>
      </Form>

      <div className="mt-8 space-y-8 pr-4">
        {axes.map((axis, axisIndex) => (
          <div key={axis.id}>
            <div className="mb-3 flex items-center gap-3 text-[18px] font-medium text-black">
              <span>{t("axis_position", { count: axisIndex + 1 })}</span>
              <CustomButton
                type="icon"
                buttonSize="small"
                icon={<FiPlusCircle size={20} />}
                className="!text-primary !bg-transparent"
                aria-label={t("add_tire_config")}
                onClick={() => openAxisModal(axisIndex)}
              />
            </div>

            <div className="overflow-hidden bg-white">
              <CustomTable<TirePresetConfigRow>
                customClass="customers-table"
                columns={columns}
                data={axis.rows}
                rowKey={(_row, rowIndex) => `${axis.id}-${rowIndex}`}
                pageSize={axis.rows.length || 10}
                currentPage={0}
                totalCount={axis.rows.length}
                showSearchInput={false}
                showCustomizeColumns={false}
                showDownload={false}
                isShowPagination={false}
                locale={{ emptyText: t("no_data") }}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 flex flex-wrap justify-center gap-3 pr-4">
        <CustomButton
          type="secondary"
          danger
          label={t("cancel")}
          buttonSize="medium"
          className="!min-w-[120px]"
          onClick={closeConfigScreen}
        />
        <CustomButton
          type="secondary"
          label={t("reset")}
          buttonSize="medium"
          className="!min-w-[120px]"
          onClick={handleReset}
        />
        <CustomButton
          type="primary"
          label={t("submit")}
          buttonSize="medium"
          loading={createMutation.isPending || updateMutation.isPending}
          className="!min-w-[120px]"
          onClick={handleSubmit}
        />
      </div>

      <AddTireConfigModal
        open={isConfigModalOpen}
        initialValues={editingRow?.values ?? null}
        onClose={() => {
          setEditingRow(null);
          setTargetAxisIndex(null);
          setIsConfigModalOpen(false);
        }}
        onSubmit={handleConfigSubmit}
      />
    </section>
  );
};

export default TirePresetConfig;
